import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, EyeIcon, EyeOffIcon, LockKeyholeIcon, MailIcon, ShieldCheckIcon, UserIcon } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { RDReadyLogo } from '../../RDReadyLogo';
type AuthMode = 'login' | 'signup';
function mapAuthError(err: unknown): string {
  const code = typeof err === 'object' && err && 'code' in err ? String(err.code) : '';
  if (code === 'auth/email-already-in-use') {
    return 'An account with this email already exists. Try signing in instead.';
  }
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'Invalid email or password. Please try again.';
  }
  if (code === 'auth/weak-password') {
    return 'Password must be at least 6 characters.';
  }
  if (code === 'auth/invalid-email') {
    return 'Please enter a valid email address.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  return 'Something went wrong. Please try again.';
}
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
function RDReadyMark() {
  return <Link to="/" className="inline-flex rounded-lg bg-white px-3 py-2 shadow-sm" aria-label="RD Ready home" data-id="element-1851" data-mp="2kwi5zdkjlrxt">
      <RDReadyLogo className="h-8 w-36" data-id="element-1852" data-mp="2fwnbz15h0m8j" />
    </Link>;
}
function PasswordField({
  value,
  onChange,
  minLength,
  placeholder
}: {
  value: string;
  onChange: (value: string) => void;
  minLength?: number;
  placeholder: string;
}) {
  const [visible, setVisible] = useState(false);
  return <div className="relative" data-id="element-1853" data-mp="967i013x4iq5">
      <LockKeyholeIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" data-id="element-1854" data-mp="96fs5l8np9xn" />
      <input id="password" name="password" type={visible ? 'text' : 'password'} required minLength={minLength} value={value} onChange={event => onChange(event.target.value)} className="w-full rounded-xl border border-[#E2E8F0] bg-white py-3.5 pl-11 pr-12 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15" placeholder={placeholder} autoComplete={minLength ? 'new-password' : 'current-password'} data-id="element-1855" data-mp="1ynbdvjwvlvqy" />
      <button type="button" onClick={() => setVisible(current => !current)} className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-[#94A3B8] transition-colors hover:text-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]" aria-label={visible ? 'Hide password' : 'Show password'} data-id="element-1856" data-mp="28efpqar990hk">
        {visible ? <EyeOffIcon className="h-4 w-4" data-id="element-1857" data-mp="tgbi2lqrhlln" /> : <EyeIcon className="h-4 w-4" data-id="element-1858" data-mp="tixrkqx7qb6i" />}
      </button>
    </div>;
}
function AuthMessage({
  type,
  children
}: {
  type: 'error' | 'success';
  children: React.ReactNode;
}) {
  const isSuccess = type === 'success';
  return <div className={`flex gap-3 rounded-xl border p-4 text-sm leading-relaxed ${isSuccess ? 'border-[#C7EFE8] bg-[#F0FBF9] text-[#0F766E]' : 'border-[#FBD5D0] bg-[#FEF2F2] text-[#B91C1C]'}`} role={isSuccess ? 'status' : 'alert'} data-id="element-1859" data-mp="yfwhex9ufvyw">
      {isSuccess ? <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1860" data-mp="kgepwa55y4lu" /> : <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1861" data-mp="kiho3hggz41n" />}
      <p data-id="element-1862" data-mp="fownrirelxza">{children}</p>
    </div>;
}
function AuthFrame({
  mode,
  children
}: {
  mode: AuthMode | 'reset';
  children: React.ReactNode;
}) {
  const title = mode === 'login' ? 'Welcome back.' : mode === 'signup' ? 'Set up your study space.' : 'Reset your password.';
  const description = mode === 'login' ? 'Sign in to continue your focused CNSC preparation.' : mode === 'signup' ? 'Create your account to keep your study access and progress in one place.' : 'We’ll send a secure reset link to your email address.';
  return <div className="w-full bg-white" style={{
    fontFamily: FONT
  }} data-id="element-1863" data-mp="307ge7ne66om0">
      <div className="mx-auto grid min-h-[calc(100vh-72px)] max-w-[1440px] lg:grid-cols-[.95fr_1.05fr]" data-id="element-1864" data-mp="354ge1gkfev4o">
        <aside className="relative hidden overflow-hidden px-10 py-12 text-white lg:flex lg:flex-col xl:px-16" style={{
        backgroundColor: BLUE
      }} data-id="element-1865" data-mp="3a3b2oz52rdwp">
          <RDReadyMark data-id="element-1866" data-mp="3j3zkm4xl9yev" />

          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          ease: [0.2, 0.65, 0.3, 0.9]
        }} className="my-auto max-w-md" data-id="element-1867" data-mp="3j36jpklba34n">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/70" data-id="element-1868" data-mp="1cfz5ua9df3tg">
              Member study space
            </p>
            <h1 className="mt-5 text-5xl font-extrabold leading-[1.04] tracking-tight text-white xl:text-6xl" data-id="element-1869" data-mp="17fupqlcdkb3r">
              Make every study session count.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-white/85" data-id="element-1870" data-mp="205bacwtr6npf">
              Return to your quizzes, active flashcards, and personalized topic
              review whenever you’re ready.
            </p>

            <div className="mt-12 border-y border-white/20 py-7" data-id="element-1871" data-mp="1r43gqs26mb1o">
              {['Practice questions with rationales', 'Active-recall flashcard review', 'Clear progress across key topics'].map((item, index) => <div key={item} className="flex items-center gap-4 py-2.5 text-sm text-white/90" data-id="element-1872" data-mp="aqvh357vp7b1" data-mpi={index}>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-[10px] font-bold text-white" data-id="element-1873" data-mp="h7wzqicf4t8q">
                    0{index + 1}
                  </span>
                  {item}
                </div>)}
            </div>
          </motion.div>

          <p className="max-w-sm text-xs leading-relaxed text-white/70" data-id="element-1874" data-mp="3pnqeosr3pwno">
            RD Ready is an independent study resource and is not affiliated with
            or endorsed by the NBNSC.
          </p>
        </aside>

        <main className="flex min-h-[calc(100vh-72px)] items-center px-5 py-12 sm:px-8 lg:px-14 xl:px-20" data-id="element-1875" data-mp="3fq2fnqmgq2zp">
          <div className="mx-auto w-full max-w-[430px]" data-id="element-1876" data-mp="3knvjwe47gmxc">
            <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            ease: [0.2, 0.65, 0.3, 0.9]
          }} data-id="element-1877" data-mp="3kmaq4qppt80j">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{
              color: BLUE
            }} data-id="element-1878" data-mp="2n4432xzaxbtk">
                {mode === 'login' ? 'Member sign in' : mode === 'signup' ? 'Create account' : 'Account recovery'}
              </p>
              <h2 className="mt-4 text-4xl font-extrabold leading-tight tracking-tight text-[#0F172A] sm:text-5xl" data-id="element-1879" data-mp="362q3eamzuywf">
                {'Welcome back!'}
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#5A6B7B]" data-id="element-1880" data-mp="3frro2e4hi4gq">
                {'Sign in to continue your dietitian journey!'}
              </p>
            </motion.div>

            <div className="mt-9" data-id="element-1881" data-mp="3tp6i5w5on0f1">{children}</div>

            <p className="mt-9 border-t border-[#EDF1F7] pt-5 text-center text-xs leading-relaxed text-[#94A3B8]" data-id="element-1882" data-mp="3otr2qzs10wgi">
              Need study access?{' '}
              <Link to="/cnsc-exam" className="font-bold underline underline-offset-4" style={{
              color: BLUE
            }} data-id="element-1883" data-mp="14ykfqyt7c5cp">
                Explore RD Ready study access
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>;
}
function AuthForm({
  mode,
  redirectTo
}: {
  mode: AuthMode;
  redirectTo?: string;
}) {
  const navigate = useNavigate();
  const {
    signIn,
    signUp
  } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isLogin = mode === 'login';
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!isLogin && !name.trim()) {
      setError('Please enter your name.');
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        await signIn(email, password);
        navigate(redirectTo ?? '/account', {
          replace: true
        });
      } else {
        await signUp(email, password, name.trim(), marketingOptIn);
        navigate(redirectTo ?? '/account', {
          replace: true
        });
      }
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1884" data-mp="2ygrs96c17ifs">
      {!isLogin && <div data-id="element-1885" data-mp="2ti87vad36epj">
          <label htmlFor="name" className="mb-2 block text-xs font-bold text-[#334155]" data-id="element-1886" data-mp="2tiqbis22vv7h">
            Full name
          </label>
          <div className="relative" data-id="element-1887" data-mp="2pipj23i59jhf">
            <UserIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" data-id="element-1888" data-mp="2kh09ytehffvz" />
            <input id="name" name="name" type="text" required value={name} onChange={event => setName(event.target.value)} className="w-full rounded-xl border border-[#E2E8F0] bg-white py-3.5 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15" placeholder="Your name" autoComplete="name" data-id="element-1889" data-mp="2qnk4xvnvrjz7" />
          </div>
        </div>}

      <div data-id="element-1890" data-mp="2mna0wojnf3mz">
        <label htmlFor="email" className="mb-2 block text-xs font-bold text-[#334155]" data-id="element-1891" data-mp="2mjvy5f72osf0">
          Email address
        </label>
        <div className="relative" data-id="element-1892" data-mp="1uoqgtukl590">
          <MailIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" data-id="element-1893" data-mp="1xufya742959" />
          <input id="email" name="email" type="email" required value={email} onChange={event => setEmail(event.target.value)} className="w-full rounded-xl border border-[#E2E8F0] bg-white py-3.5 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15" placeholder="you@example.com" autoComplete="email" data-id="element-1894" data-mp="4fed4kgnro4n" />
        </div>
      </div>

      <div data-id="element-1895" data-mp="eebwza352thb">
        <div className="mb-2 flex items-center justify-between gap-4" data-id="element-1896" data-mp="eelm6vgd0wly">
          <label htmlFor="password" className="text-xs font-bold text-[#334155]" data-id="element-1897" data-mp="ec95smq92ub0">
            Password
          </label>
          {isLogin && <Link to="/forgot-password" className="text-xs font-bold underline underline-offset-4" style={{
          color: BLUE
        }} data-id="element-1898" data-mp="1648bi0t71bgu">
              Forgot password?
            </Link>}
        </div>
        <PasswordField value={password} onChange={setPassword} minLength={isLogin ? undefined : 6} placeholder={isLogin ? 'Enter your password' : 'At least 6 characters'} data-id="element-1899" data-mp="1gry78ldg9xxd" />
      </div>

      {!isLogin && <label className="flex cursor-pointer items-start gap-3 pt-1 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-1900" data-mp="11tm90aafih43">
          <input type="checkbox" checked={marketingOptIn} onChange={event => setMarketingOptIn(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2563EB]" data-id="element-1901" data-mp="x1l08ycgz8it" />
          <span data-id="element-1902" data-mp="o3skxu6r00vf">
            Send me occasional RD Ready study updates. You can unsubscribe at
            any time.
          </span>
        </label>}

      {error && <AuthMessage type="error" data-id="element-1903" data-mp="e4tac62f9v5s">{error}</AuthMessage>}

      <button type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-xs font-bold uppercase tracking-[0.14em] text-white shadow-sm transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0" style={{
      backgroundColor: BLUE
    }} data-id="element-1904" data-mp="e64hmfv2tl1n">
        {loading ? <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-1905" data-mp="3hg2gjv7mdp0p" />
            {isLogin ? 'Signing in…' : 'Creating account…'}
          </> : <>
            {isLogin ? 'Sign in to study' : 'Create my account'}
            <ArrowRightIcon className="h-4 w-4" data-id="element-1906" data-mp="3w45r7hwlxovu" />
          </>}
      </button>

      <p className="pt-1 text-center text-sm text-[#5A6B7B]" data-id="element-1907" data-mp="2yoi0ttsl5b4p">
        {isLogin ? 'New to RD Ready?' : 'Already have an account?'}{' '}
        <Link to={isLogin ? '/signup' : '/login'} state={redirectTo ? {
        from: redirectTo
      } : undefined} className="font-bold underline underline-offset-4" style={{
        color: BLUE
      }} data-id="element-1908" data-mp="33m1sbveankel">
          {isLogin ? 'Create an account' : 'Sign in'}
        </Link>
      </p>
    </form>;
}
export function Login() {
  const location = useLocation();
  const redirectTo = (location.state as {
    from?: string;
  } | null)?.from ?? '/account';
  return <AuthFrame mode="login" data-id="element-1909" data-mp="1nvr39em6tt1k">
      <AuthForm mode="login" redirectTo={redirectTo} data-id="element-1910" data-mp="21vs3crumowfz" />
    </AuthFrame>;
}
export function Signup() {
  const location = useLocation();
  const redirectTo = (location.state as {
    from?: string;
  } | null)?.from ?? '/account';
  return <AuthFrame mode="signup" data-id="element-1911" data-mp="7ian7pwjz1uz">
      <AuthForm mode="signup" redirectTo={redirectTo} data-id="element-1912" data-mp="7jlptme9i3ki" />
    </AuthFrame>;
}
export function ForgotPassword() {
  const {
    resetPassword
  } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <AuthFrame mode="reset" data-id="element-1913" data-mp="20feeg85pc5g7">
      {sent ? <div className="space-y-6" data-id="element-1914" data-mp="1lhdmfp7y2drc">
          <AuthMessage type="success" data-id="element-1915" data-mp="1lif6ukauoclq">
            If an account exists for <strong data-id="element-1916" data-mp="1qg6xlazrdsoc">{email}</strong>, we’ve sent a
            password reset link. Check your spam folder if you don’t see it.
          </AuthMessage>
          <Link to="/login" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] hover:opacity-80" style={{
        color: BLUE
      }} data-id="element-1917" data-mp="3fvwndncu8979">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-1918" data-mp="3jay7niaz4pxk" />
            Back to sign in
          </Link>
        </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1919" data-mp="39ev202gwy95v">
          <div data-id="element-1920" data-mp="39h7j6deukfhv">
            <label htmlFor="email" className="mb-2 block text-xs font-bold text-[#334155]" data-id="element-1921" data-mp="39gy0zc01ncmq">
              Email address
            </label>
            <div className="relative" data-id="element-1922" data-mp="60imrk8zam5x">
              <MailIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" data-id="element-1923" data-mp="yt9rz273yf7" />
              <input id="email" name="email" type="email" required value={email} onChange={event => setEmail(event.target.value)} className="w-full rounded-xl border border-[#E2E8F0] bg-white py-3.5 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15" placeholder="you@example.com" autoComplete="email" data-id="element-1924" data-mp="3ocdtvdow12hj" />
            </div>
          </div>

          {error && <AuthMessage type="error" data-id="element-1925" data-mp="2gkgf8wbdh500">{error}</AuthMessage>}

          <button type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-xs font-bold uppercase tracking-[0.14em] text-white shadow-sm transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0" style={{
        backgroundColor: BLUE
      }} data-id="element-1926" data-mp="2glzwap4urk2u">
            {loading ? <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-1927" data-mp="34c412zts1pvf" />
                Sending link…
              </> : <>
                Send reset link
                <ArrowRightIcon className="h-4 w-4" data-id="element-1928" data-mp="2f744irkae21w" />
              </>}
          </button>

          <p className="pt-1 text-center text-sm text-[#5A6B7B]" data-id="element-1929" data-mp="2t6wq68gyhs30">
            Remembered it?{' '}
            <Link to="/login" className="font-bold underline underline-offset-4" style={{
          color: BLUE
        }} data-id="element-1930" data-mp="2p5bir2n6y607">
              Back to sign in
            </Link>
          </p>
        </form>}
    </AuthFrame>;
}